import { type JSX, useEffect, useMemo, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useAccount } from 'wagmi';
import { AnimatedTextInput } from '@/shared/components/AnimatedTextInput';
import { Link } from '@/shared/components/atoms/Link';
import {
  DashboardModalWithCloseButton,
  ModalContent,
  StatsPanel,
} from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import Checkbox from '@/shared/components/flip-ui-kit/Checkbox';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import CopyButton from '@/shared/components/molecules/CopyButton';
// import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import TokenInputField from '@/shared/components/TokenInputField';
import WalletActionButton from '@/shared/components/WalletActionButton';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import {
  useApproveStateChainGateway,
  useFlipBalance,
  useFundAccount,
} from '@/shared/hooks/funding';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import useToast from '@/shared/hooks/useToast';
import useTracking from '@/shared/hooks/useTracking';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/large/WarningTriangle';
import WindowIcon from '@/shared/icons/flip-ui-kit/large/Window';
import DistanceIcon from '@/shared/icons/flip-ui-kit/small/Distance';
import DollarIcon from '@/shared/icons/flip-ui-kit/small/Dollar';
import KeyIcon from '@/shared/icons/flip-ui-kit/small/Key';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/small/PlusCircle';
import { buildChainflipExplorerLink } from '@/shared/utils/chainflip';
import { chainflipAssetMap, FLIP_SYMBOL } from '@/shared/utils/env';
import { getViemError } from '@/shared/utils/functions';
import { isChainflipSs58Address } from '@/shared/utils/helpers';
import makeRpcRequest from '@/shared/utils/rpc';
import TokenAmount from '@/shared/utils/TokenAmount';
import { StakeEvents, type StakeTrackEvents } from '../../../types/track';

const NumberCircle = ({ children }: { children: string }) => (
  <div className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-cf-gray-4 font-aeonikMedium text-14 text-cf-light-1 group-enabled:mix-blend-hard-light">
    {children}
  </div>
);

// we need to keep the auction and account info in sync
// the RPC requests will be batched so we get info at the same block
const useValidatorAndAuction = (accountId: string) => {
  const validatedAccountId = useMemo(
    () => (isChainflipSs58Address(accountId) ? accountId : null),
    [accountId],
  );

  return useQuery({
    queryKey: ['validator-and-auction', isChainflipSs58Address(accountId) && accountId],
    queryFn: async () => {
      const [accountInfo, auctionState] = await Promise.all([
        validatedAccountId ? makeRpcRequest('cf_account_info', accountId) : null,
        makeRpcRequest('cf_auction_state'),
      ]);

      return {
        flipBalance: TokenAmount.fromAsset(accountInfo?.flip_balance ?? 0, 'Flip'),
        minBid: TokenAmount.fromAsset(auctionState?.min_bid ?? 0, 'Flip'),
        minActiveBid: TokenAmount.fromAsset(auctionState?.min_active_bid ?? 0, 'Flip'),
      };
    },
    refetchInterval: TABLE_POLL_INTERVAL,
  });
};

export const RegisterNodeModalContent = ({
  nodeAddress,
  setNodeAddress,
  onFunded,
}: {
  nodeAddress: string;
  setNodeAddress?: (value: string) => void;
  onFunded: (amount: TokenAmount) => void;
}) => {
  const track = useTracking<StakeTrackEvents>();
  const {
    data: {
      minActiveBid = TokenAmount.ZERO,
      minBid = TokenAmount.ZERO,
      flipBalance: currentBalance = TokenAmount.ZERO,
    } = {},
  } = useValidatorAndAuction(nodeAddress);

  const { data: flipBalance } = useFlipBalance();

  const { address: connectedAddress } = useAccount();
  const toast = useToast('funding');
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [flipAmount, setFlipAmount] = useState<TokenAmount>();
  const [nodeAddressErrorMessage, setNodeAddressErrorMessage] = useState('');
  const [hasAmountError, setHasAmountError] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const { data: accountInfo } = useRpcRequest({
    method: 'cf_account_info',
    args: [nodeAddress],
    enabled: nodeAddress !== '',
  });

  const buttonsDisabled =
    hasAmountError ||
    !flipAmount ||
    Boolean(nodeAddressErrorMessage) ||
    !warningAcknowledged ||
    !nodeAddress;

  useEffect(() => {
    if (nodeAddress === '') {
      setNodeAddressErrorMessage('');
      return;
    }

    if (nodeAddress.startsWith('0x') || !isChainflipSs58Address(nodeAddress)) {
      setNodeAddressErrorMessage('Please enter a valid Chainflip SS58 validator key');
      return;
    }

    setNodeAddressErrorMessage('');
  }, [nodeAddress]);

  const { mutate: approveFlip, isPending: isApproving } = useApproveStateChainGateway({
    onError(error) {
      toast.error('Failed to approve', {
        description: getViemError(error),
      });
    },
    onSuccess() {
      toast.success(`${flipAmount?.toFormatted()} ${FLIP_SYMBOL} approved`);
      setIsApproved(true);
    },
  });

  const approve = async (_: React.MouseEvent) => {
    if (!flipAmount) {
      throw new Error('Flip amount is not set');
    }
    track(StakeEvents.CtaRegisterApproveToken, {
      props: {
        connectedWallet: connectedAddress as string,
        scAccount: nodeAddress,
        path: window.location.pathname,
        amount: flipAmount?.toFormatted(),
      },
    });

    toast.loading('Waiting for token approval...');

    approveFlip(flipAmount.toBigInt());
  };

  const { mutate: fundStateChainAccount, isPending: isFunding } = useFundAccount({
    onError(error) {
      toast.error('Failed to fund account', {
        description: getViemError(error),
      });
    },
    onSuccess() {
      track(StakeEvents.RegisterNodeSuccess, {
        props: {
          connectedWallet: connectedAddress as string,
          scAccount: nodeAddress,
          path: window.location.pathname,
          amount: flipAmount?.toFormatted() ?? '',
        },
      });
      onFunded(flipAmount!);
    },
  });

  const fundAccount = async (_: React.MouseEvent) => {
    if (!flipAmount) {
      toast.error('Flip amount is not set');
      return;
    }
    track(StakeEvents.CtaRegisterAddFunds, {
      props: {
        connectedWallet: connectedAddress as string,
        scAccount: nodeAddress,
        path: window.location.pathname,
        amount: flipAmount?.toFormatted(),
      },
    });

    fundStateChainAccount({
      accountId: nodeAddress as `cF${string}`,
      amount: flipAmount.toBigInt(),
    });
  };

  const totalBid = flipAmount?.add(currentBalance) ?? currentBalance;
  const distanceFromMAB = totalBid.sub(minActiveBid);

  return (
    <ModalContent
      title={accountInfo?.role === 'validator' ? 'Add funds' : 'Register new node'}
      buttons={
        <div className="flex w-full flex-col gap-y-4">
          <WalletActionButton
            requireConnection
            chainId={chainflipAssetMap.Flip.chain.id}
            onClick={approve}
            disabled={isApproved || isApproving || buttonsDisabled}
            fullWidth
            loading={isApproving && 'iconOnly'}
            className="group"
          >
            <NumberCircle>1</NumberCircle>
            <span className="ml-[5px]">Approve {FLIP_SYMBOL}</span>
          </WalletActionButton>
          <WalletActionButton
            requireConnection
            chainId={chainflipAssetMap.Flip.chain.id}
            onClick={fundAccount}
            disabled={!isApproved || isFunding || buttonsDisabled}
            fullWidth
            loading={isFunding && 'iconOnly'}
            className="group"
          >
            <NumberCircle>2</NumberCircle>
            <span className="ml-[5px]">Add funds</span>
          </WalletActionButton>
        </div>
      }
    >
      <div className="flex flex-col gap-y-6">
        <div className="flex w-full items-center rounded-alert border border-[#684C17] bg-[#463719] p-2 text-12 text-cf-orange-1">
          <WarningTriangleIcon />
          <div className="ml-2 font-aeonikMedium text-14">
            Don&apos;t add funds for a validator you don&apos;t control or else your funds will be
            lost!
          </div>
        </div>
        <AnimatedTextInput
          label="Validator Public Key (SS58)"
          onChange={setNodeAddress}
          error={nodeAddressErrorMessage}
          value={nodeAddress}
        />
        <div className="-mt-2 h-px w-full bg-cf-gray-4" />
        <TokenInputField
          value={flipAmount}
          onChange={setFlipAmount}
          token={chainflipAssetMap.Flip}
          balance={flipBalance}
          tokenWidth={180}
          acceptOverflowAmount
          setHasError={setHasAmountError}
        />
        <div className="flex flex-col gap-y-2 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-13 text-cf-light-2">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-1">
              <DollarIcon />
              Min. Active Bid
              {/* <QuestionMarkTooltip content="TODO(delegation)" /> */}
            </div>
            <div className="flex items-center gap-x-1 text-cf-light-3">
              {minActiveBid.toFormatted()} <TokenLogo token={chainflipAssetMap.Flip} />{' '}
              {FLIP_SYMBOL}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex items-center gap-x-1">
              <DollarIcon />
              Min. stake
              {/* <QuestionMarkTooltip content="TODO(delegation)" /> */}
            </div>
            <div className="flex items-center gap-x-1 text-cf-light-3">
              {minBid.toFormatted()} <TokenLogo token={chainflipAssetMap.Flip} /> {FLIP_SYMBOL}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex items-center gap-x-1">
              <DistanceIcon />
              New Distance from MAB
              {/* <QuestionMarkTooltip content="TODO(delegation)" /> */}
            </div>
            <div className="flex items-center gap-x-1 text-cf-light-3">
              <span
                className={classNames(distanceFromMAB?.lt(0) ? 'text-cf-red-2' : 'text-cf-green-3')}
              >
                {distanceFromMAB?.toFormatted()}
              </span>{' '}
              <TokenLogo token={chainflipAssetMap.Flip} /> {FLIP_SYMBOL}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3 font-aeonikMedium text-14 text-cf-light-3">
          <Checkbox
            id="checker"
            checked={warningAcknowledged}
            setChecked={setWarningAcknowledged}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="checker" className="cursor-pointer">
            I acknowledge that funding a validator I don&apos;t control will result in loss of
            funds.
          </label>
        </div>
      </div>
    </ModalContent>
  );
};

export const RegisterNodeModal = ({
  open,
  onClose,
  accountId,
}: {
  onClose: () => void;
  open: boolean;
  accountId?: `cF${string}` | undefined;
}): JSX.Element => {
  const [step, setStep] = useState<'fund' | 'success'>('fund');
  const [nodeAddress, setNodeAddress] = useState(accountId ?? '');
  const [flipAmount, setFlipAmount] = useState<TokenAmount>();

  const reset = () => {
    setStep('fund');
    setNodeAddress('');
    setFlipAmount(TokenAmount.ZERO);
  };

  useEffect(() => {
    if (open) setNodeAddress(accountId ?? '');
  }, [open]);

  const blockExplorerLink = buildChainflipExplorerLink(nodeAddress, 'validators');

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose} afterLeave={reset}>
      {step === 'success' ? (
        <ModalContent success title={accountId ? 'Funds Added!' : 'Validator Registered!'}>
          <div className="flex flex-col gap-4">
            <StatsPanel
              items={[
                {
                  Icon: KeyIcon,
                  label: 'Validator Public Key (SS58)',
                  content: (
                    <div className="flex items-center gap-x-1 text-cf-light-3">
                      {abbreviate(nodeAddress)}
                      <CopyButton textToCopy={nodeAddress} />
                    </div>
                  ),
                },
                {
                  Icon: PlusCircleIcon,
                  label: 'Funds Added',
                  flipAmount,
                },
              ]}
            />
            <Button size="medium" type="primary-standard" onClick={onClose}>
              View my Validator
            </Button>
            <Link
              href={blockExplorerLink}
              className="flex w-full items-center justify-center space-x-2 text-14 text-cf-light-3"
              target="_blank"
            >
              <span className="font-aeonikRegular text-14">View on Explorer</span>
              <WindowIcon className="text-cf-light-3" />
            </Link>
          </div>
        </ModalContent>
      ) : (
        <RegisterNodeModalContent
          nodeAddress={nodeAddress}
          setNodeAddress={accountId === undefined ? setNodeAddress : undefined}
          onFunded={(amount) => {
            setFlipAmount(amount);
            setStep('success');
          }}
        />
      )}
    </DashboardModalWithCloseButton>
  );
};
